<script setup>
import { BCol, BRow } from 'bootstrap-vue-next';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ConnectCommunitySvg from '@images/chat-sm.svg';
import JoinEventSvg from '@images/empty-schedule-md.svg';
import EmptySearchSvg from '@images/empty-search-md.svg';
import StartOnboardingSvg from '@images/empty-todos-md.svg';
import LoginSvg from '@images/empty-user-md.svg';
import ReadGuideSvg from '@images/empty-wiki-md.svg';
import AiPoweredSvg from '@images/tanuki-ai-md.svg';

import LevelProgress from '~/common/components/LevelProgress.vue';
import { useVueData } from '~/common/useVueData';

import ActionCard from './home-page/ActionCard.vue';
import CommunityHighlights from './home-page/CommunityHighlights.vue';
import ContributionChecklist from './home-page/ContributionChecklist.vue';
import HackathonBanner from './home-page/HackathonBanner.vue';
import StartOnboardingModal from './home-page/StartOnboardingModal.vue';

const { approvedCommunityMember, onboardingIssueUrl, isSignedIn: signedIn, teamMember, hasDiscordMessages, contributorLevel, runningHackathon } = useVueData();
const onboardingStarted = onboardingIssueUrl != null;

const referrer = ref(null);
const showStartOnboardingModal = ref(false);

const route = useRoute();
const router = useRouter();

const displayOnboardingModal = () => {
  if (route.hash !== '#start') {
    return;
  }

  if (!signedIn) {
    location.href = '/start';

    return;
  }

  referrer.value = route.query?.referrer ? decodeURIComponent(route.query.referrer) : null;
  showStartOnboardingModal.value = true;
  router.replace({
    name: route.name,
    query: {},
    hash: '',
  });
};

onMounted(() => {
  displayOnboardingModal();
});

watch(() => route.hash, () => {
  displayOnboardingModal();
});
</script>

<template>
  <p>
    Join 4,000+ contributors in GitLab's open source community and co-create the platform millions of people use to deliver software faster.
  </p>
  <HackathonBanner
    v-if="runningHackathon"
    :hackathon="runningHackathon"
    class="mb-3"
  />
  <BRow
    v-if="signedIn && !teamMember"
    class="my-3"
  >
    <BCol>
      <LevelProgress />
    </BCol>
  </BRow>
  <BRow>
    <BCol
      v-if="!signedIn"
      cols="12"
      md="6"
      class="mb-3"
    >
      <ActionCard
        title="Login"
        action-link="/login"
        action-name="Login"
        button-variant="primary"
        :logo-path="LoginSvg"
        :target-blank="false"
      >
        Please login to help us tailor your experience.
      </ActionCard>
    </BCol>
    <BCol
      v-if="!approvedCommunityMember && !onboardingStarted"
      cols="12"
      md="6"
      class="mb-3"
    >
      <ActionCard
        title="Start onboarding"
        action-name="Start onboarding"
        :button-variant="signedIn ? 'primary' : 'outline-primary'"
        :logo-path="StartOnboardingSvg"
        :to="{ hash: '#start' }"
      >
        Start onboarding to contribute to GitLab and get access to GitLab Duo AI.
      </ActionCard>
    </BCol>
    <BCol
      v-if="signedIn"
      cols="12"
      md="6"
      class="mb-3"
    >
      <ContributionChecklist />
    </BCol>
    <BCol
      cols="12"
      md="6"
      class="mb-3"
    >
      <ActionCard
        title="AI powered"
        action-link="https://docs.gitlab.com/user/gitlab_duo/"
        action-name="Learn more"
        button-variant="outline-primary"
        :logo-path="AiPoweredSvg"
      >
        <p>
          GitLab Duo Agent Platform is included for all contributors to help boost your efficiency and effectiveness.
        </p>
        <template v-if="signedIn">
          <p>
            Use GitLab Duo in:
          </p>
          <ul>
            <li>
              <a
                href="https://gitlab.com"
                target="_blank"
              >The GitLab UI</a>
            </li>
            <li>
              <a
                href="https://docs.gitlab.com/user/gitlab_duo_cli/"
                target="_blank"
              >The GitLab Duo CLI</a>
            </li>
            <li>
              <a
                href="https://docs.gitlab.com/user/project/repository/code_suggestions/supported_extensions/"
                target="_blank"
              >Supported IDEs</a>
            </li>
            <li>
              <a
                href="https://opencode.ai/"
                target="_blank"
              >OpenCode</a>
            </li>
          </ul>
        </template>
        <p v-else>Code Suggestions, Chat, Root Cause Analysis and more!</p>
      </ActionCard>
    </BCol>
    <BCol
      cols="12"
      md="6"
      class="mb-3"
    >
      <ActionCard
        title="Find an issue"
        to="/issues"
        action-name="Get started"
        :button-variant="signedIn && approvedCommunityMember ? 'primary' : 'outline-primary'"
        :logo-path="EmptySearchSvg"
      >
        Use the issue finder to find issues by label, type, and category.
      </ActionCard>
    </BCol>
    <BCol
      v-if="!hasDiscordMessages"
      cols="12"
      md="6"
      class="mb-3"
    >
      <ActionCard
        title="Connect with the community"
        action-link="https://discord.gg/gitlab"
        action-name="Join Discord"
        :logo-path="ConnectCommunitySvg"
      >
        Team members and community members hangout in the <code>#contribute</code> channel on the GitLab Discord server.
      </ActionCard>
    </BCol>
    <BCol
      v-if="!signedIn || contributorLevel < 2"
      cols="12"
      md="6"
      class="mb-3"
    >
      <ActionCard
        title="Read the guide"
        action-link="https://docs.gitlab.com/development/contributing/"
        action-name="Read the guide"
        :logo-path="ReadGuideSvg"
      >
        Our contribution guide will help you learn the different ways you can contribute,
        choose a development environment, find issues to work on, make changes, and open a merge request.
      </ActionCard>
    </BCol>
    <BCol
      cols="12"
      md="6"
      class="mb-3"
    >
      <ActionCard
        title="Join an event"
        action-link="https://www.meetup.com/gitlab-virtual-meetups"
        action-name="View events"
        :logo-path="JoinEventSvg"
      >
        Register for an upcoming hackathon, pairing session, or office hour session on Meetup.com.
      </ActionCard>
    </BCol>
  </BRow>
  <CommunityHighlights class="mb-3" />
  <h1>Everyone can contribute</h1>
  <p>
    It is GitLab's mission to make it so that everyone can contribute.
    <a
      href="https://handbook.gitlab.com/handbook/values/"
      target="_blank"
    >Diversity, Inclusion, and Belonging</a>
    is part of GitLab's commitment to creating a welcoming community for every contributor.
    <a
      href="https://badging.chaoss.community/project-badging"
      target="_blank"
    >CHAOSS DEI Badge</a>
    for our
    <a
      href="https://gitlab.com/gitlab-org/gitlab/-/blob/master/DEI.md"
      target="_blank"
    >Diversity, Equity, and Inclusion Project Statement</a>,
    which documents our commitment to DEI within our open source community.
  </p>
  <p>
    Please visit our
    <a
      href="https://about.gitlab.com/community/contribute/code-of-conduct/"
      target="_blank"
    >Code of Conduct page</a>
    to learn more about our pledge and standards for an open and welcoming environment.
  </p>
  <p>
    All contributions to GitLab are subject to the
    <a
      href="https://about.gitlab.com/community/contribute/dco-cla/"
      target="_blank"
    >
      DCO or a CLA</a>.
  </p>
  <StartOnboardingModal
    v-model="showStartOnboardingModal"
    :referrer="referrer"
  />
</template>
